import classNames from 'classnames';
import { WorkAssignment } from '../../../hooks/workAssignmentHooks';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import { SharedIcon } from 'components/SharedIcon/SharedIcon';
import PratusSelect, {
  OptionType,
} from 'components/DesignSystems/New/PratusSelect';
import StylishNewInput from 'components/DesignSystems/New/StylishNewInput';
import StylishDateTimePicker from 'components/DesignSystems/form/StylishDateTimePicker';
import { formatDateTime } from 'components/IAP/Resources/utils/dateUtils';
import { WORK_ASSIGNMENT_STATUSES } from '../../../ICSConstants';
import {
  ResourceCellDraft,
  ResourceCellKey,
  WorkAssignmentDraft,
} from './workAssignment.types';

export type { WorkAssignmentDraft };

type WorkAssignmentRowProps = {
  work: WorkAssignment;
  draft: WorkAssignmentDraft | undefined;
  mode: 'view' | 'edit';
  workGroups: { id: string; name: string }[];
  resourceColumns: string[];
  resourceValues: Record<string, ResourceCellDraft>;
  readOnly?: boolean;
  onFieldChange: (field: keyof WorkAssignment, value: unknown) => void;
  onResourceChange: (
    resourceName: string,
    key: ResourceCellKey,
    value: string
  ) => void;
  onDelete: () => void;
};

const RESOURCE_ROW_HEIGHT_EDIT_CLASS = 'tw:min-h-[calc(1.5em_+_0.75rem_+_2px)]';
const RESOURCE_KEYS: ResourceCellKey[] = ['required', 'have', 'need'];
const RESOURCE_LABELS: Record<ResourceCellKey, string> = {
  required: 'REQ',
  have: 'HAVE',
  need: 'NEED',
};

const value = <K extends keyof WorkAssignment>(
  work: WorkAssignment,
  draft: WorkAssignmentDraft | undefined,
  field: K
): WorkAssignment[K] =>
  draft && field in draft ? (draft[field] as WorkAssignment[K]) : work[field];

const WorkAssignmentRow: React.FC<WorkAssignmentRowProps> = ({
  work,
  draft,
  mode,
  workGroups,
  resourceColumns,
  resourceValues,
  readOnly = false,
  onFieldChange,
  onResourceChange,
  onDelete,
}) => {
  const isEditing = mode === 'edit' && !readOnly;
  const workGroupId =
    (value(work, draft, 'work_group_id') as string | undefined) ?? '';
  const workGroupName =
    workGroups.find((g) => g.id === workGroupId)?.name ??
    (value(work, draft, 'work_group_name') as string | undefined) ??
    '';
  const status = (value(work, draft, 'status') as string) ?? '';
  const arrivalTimeRaw = value(work, draft, 'requested_arrival_time') as
    | Date
    | string
    | undefined;
  const arrivalTimeParsed = arrivalTimeRaw
    ? arrivalTimeRaw instanceof Date
      ? arrivalTimeRaw
      : new Date(arrivalTimeRaw)
    : undefined;
  const arrivalTime =
    arrivalTimeParsed && !isNaN(arrivalTimeParsed.getTime())
      ? arrivalTimeParsed
      : undefined;

  const stackRowClassName = classNames('d-flex align-items-center', {
    [RESOURCE_ROW_HEIGHT_EDIT_CLASS]: isEditing,
  });

  const renderResourceCell = (name: string) => (
    <div className="d-flex flex-column gap-1">
      {RESOURCE_KEYS.map((key) => {
        const val = resourceValues[name]?.[key] ?? '';
        if (key === 'need' || !isEditing) {
          return (
            <span key={key} className={stackRowClassName}>
              {val}
            </span>
          );
        }
        return (
          <StylishNewInput
            key={key}
            type="number"
            value={val}
            onChange={(e) => onResourceChange(name, key, e.target.value)}
          />
        );
      })}
    </div>
  );

  const renderResourceLabelCell = () => (
    <div className="d-flex flex-column gap-1">
      {RESOURCE_KEYS.map((key) => (
        <span
          key={key}
          className={stackRowClassName}
          style={{ fontSize: '0.85em' }}
        >
          {RESOURCE_LABELS[key]}
        </span>
      ))}
    </div>
  );

  return (
    <tr>
      <td>
        {isEditing ? (
          <PratusSelect
            options={workGroups.map((g) => ({ label: g.name, value: g.id }))}
            value={{ label: workGroupName, value: workGroupId }}
            onChange={(v) => {
              const opt = v as OptionType | null;
              onFieldChange('work_group_id', (opt?.value as string) ?? '');
              onFieldChange('work_group_name', (opt?.label as string) ?? '');
            }}
          />
        ) : (
          workGroupName
        )}
      </td>
      <td>
        {isEditing ? (
          <StylishNewInput
            value={value(work, draft, 'work_assignment') ?? ''}
            onChange={(e) => onFieldChange('work_assignment', e.target.value)}
            maxLength={255}
          />
        ) : (
          value(work, draft, 'work_assignment')
        )}
      </td>
      <td className="min-w-20">{renderResourceLabelCell()}</td>
      {resourceColumns.length === 0 ? (
        <td />
      ) : (
        resourceColumns.map((name) => (
          <td key={name} className="min-w-30">
            {renderResourceCell(name)}
          </td>
        ))
      )}
      <td />
      <td>
        {isEditing ? (
          <StylishNewInput
            value={value(work, draft, 'overhead_positions') ?? ''}
            onChange={(e) =>
              onFieldChange('overhead_positions', e.target.value)
            }
            maxLength={255}
          />
        ) : (
          value(work, draft, 'overhead_positions')
        )}
      </td>
      <td>
        {isEditing ? (
          <StylishNewInput
            value={value(work, draft, 'special_instructions') ?? ''}
            onChange={(e) =>
              onFieldChange('special_instructions', e.target.value)
            }
            maxLength={255}
          />
        ) : (
          value(work, draft, 'special_instructions')
        )}
      </td>
      <td>
        {isEditing ? (
          <StylishNewInput
            value={value(work, draft, 'reporting_location') ?? ''}
            onChange={(e) =>
              onFieldChange('reporting_location', e.target.value)
            }
            maxLength={255}
          />
        ) : (
          value(work, draft, 'reporting_location')
        )}
      </td>
      <td>
        {isEditing ? (
          <StylishDateTimePicker
            value={arrivalTime ?? null}
            onChange={(e) =>
              onFieldChange(
                'requested_arrival_time',
                e ? new Date(e) : undefined
              )
            }
            type="datetime-local"
          />
        ) : (
          formatDateTime(arrivalTime?.toISOString())
        )}
      </td>
      <td>
        {isEditing ? (
          <PratusSelect
            options={WORK_ASSIGNMENT_STATUSES}
            value={{ label: status, value: status }}
            onChange={(v) => {
              const opt = v as OptionType | null;
              onFieldChange('status', (opt?.value as string) ?? '');
            }}
          />
        ) : (
          status
        )}
      </td>
      <td>
        {!readOnly && (
          <StylishNewButton className="button--link" onClick={onDelete}>
            <SharedIcon iconName="delete" color="light" />
          </StylishNewButton>
        )}
      </td>
    </tr>
  );
};

export default WorkAssignmentRow;
